/*
  Single source of truth for everything the landing page links to or quotes.

  Nothing in this file is invented for the redesign. Every URL, phone number,
  video id, statistic and customer quote was already present in the project
  before the redesign and is carried over verbatim. If a claim is not here, the
  page does not make it.
*/

export const CONTACT = {
  phoneDisplay: '+233 246 805 839',
  phoneHref: 'tel:+233246805839',
};

export const APP_LINKS = {
  web: 'https://console.atadaboard.com',
  ios: 'https://apps.apple.com/us/app/atadaboard/id6464030081',
  android: 'https://play.google.com/store/apps/details?id=com.devroldy.atadaboard',
};

/*
  `icon` is the lucide-react export name for the mark. Keep it separate from
  `name`: the display name is written the way the brand writes it (LinkedIn),
  which is not how the icon is exported (Linkedin).
*/
export const SOCIAL_LINKS = [
  { name: 'Instagram', icon: 'Instagram', href: 'https://www.instagram.com/atadaboard/' },
  { name: 'Facebook', icon: 'Facebook', href: 'https://www.facebook.com/atada.board/' },
  { name: 'LinkedIn', icon: 'Linkedin', href: 'https://www.linkedin.com/company/atadaboard/' },
  { name: 'YouTube', icon: 'Youtube', href: 'https://youtube.com/@atadaboard' },
];

/* Overview film opened from the hero's secondary CTA. */
export const OVERVIEW_VIDEO_ID = 'VHNFoAE65dA';

/*
  Filmed customer stories. These are real published videos on the AtadaBoard
  YouTube channel; the roles are the ones the speakers give on camera. Durations
  are intentionally absent because they were never verified.
*/
export const VIDEO_STORIES = [
    {
    id: 'assistant-admin',
    videoId: 'EAq_jz6R3TA',
    role: 'Assistant administrator',
    headline: 'School management made smarter with AtadaBoard',
    summary:
      'An assistant administrator on how AtadaBoard makes running the school easier.',
  },
  {
    id: 'head-early-grade',
    videoId: 'nsN9v8bhci4',
    role: 'Head of Early Grade',
    headline: 'Teachers should be focused on teaching, not paperwork',
    summary:
      'The head of early grade on freeing teachers from paperwork so they can focus on the classroom.',
  },
  {
    id: 'head-teacher',
    videoId: 'HMi1HPFZLdo',
    role: 'Head teacher',
    headline: 'What smarter school management looks like in practice',
    summary:
      'A head teacher on what day-to-day school management looks like with AtadaBoard.',
  },
  {
    id: 'school-admin',
    videoId: 'GJn7LqA8Aec',
    role: 'School administrator',
    headline: 'How AtadaBoard simplifies leadership and records',
    summary:
      'An administrator walks through running school records and day-to-day leadership tasks in one place.',
  },
  {
    id: 'parent-experience',
    videoId: 'Z6xs65YTKTQ',
    role: 'Parent',
    headline: 'Better communication and transparency for families',
    summary:
      'A parent on what changed at home once school updates, reports and bills arrived in one app.',
  },
  {
    id: 'facilitator',
    videoId: '0qPJQokJnHM',
    role: 'School facilitator',
    headline: 'One system to manage the whole school',
    summary:
      'A facilitator covers the parts of school operations AtadaBoard replaced.',
  },
  {
    id: 'guardian',
    videoId: 'mtHzK8ngxb4',
    role: 'Guardian',
    headline: 'The app every parent needs for school communication',
    summary:
      'A guardian on staying in touch with the school without chasing anyone.',
  },
  {
    id: 'parent',
    videoId: 'WItfSKejbwg',
    role: 'Parent',
    headline: 'I know everything about my children’s education',
    summary:
      'A parent describes the visibility they now have into their children’s school life.',
  },
];

export const youTubeThumb = (videoId) =>
  `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

export const youTubeEmbed = (videoId) =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;

/*
  Written customer quotes carried over from the previous site, unchanged.
  Presentation is new; the words, names, roles and schools are not.
*/
export const TESTIMONIALS = [
  {
    name: 'Dr. Sarah Mitchell',
    role: 'Principal',
    school: 'Green Valley International School',
    location: 'Accra',
    quote:
      'AtadaBoard completely revolutionized our school operations. What used to take our admin team hours now happens in minutes.',
  },
  {
    name: 'Michael Asante',
    role: 'Finance administrator',
    school: 'Sunrise Academy',
    location: 'Kumasi',
    quote:
      'The billing and payment system is a game-changer. We have reduced payment collection time from weeks to days, and parents appreciate the convenience of online payments.',
  },
  {
    name: 'Mrs. Akosua Frempong',
    role: 'Academic coordinator',
    school: 'Excellence Educational Institute',
    location: 'Takoradi',
    quote:
      'Managing student records and generating reports used to be our biggest headache. We can track individual student progress, identify learning gaps, and make decisions that improve student outcomes.',
  },
  {
    name: 'James Osei',
    role: 'IT administrator',
    school: 'Future Leaders Academy',
    location: 'Tema',
    quote:
      'Implementation was surprisingly smooth. The AtadaBoard team guided us through every step, and our staff adapted quickly to the interface.',
  },
  {
    name: 'Mrs. Comfort Adjei',
    role: 'Parent and teacher',
    school: 'Bright Minds School',
    location: 'Cape Coast',
    quote:
      'The teacher portal makes lesson planning and grade management effortless, while the parent app keeps me connected to my own child’s school activities.',
  },
  {
    name: 'David Mensah',
    role: 'Operations manager',
    school: 'International Gateway School',
    location: 'East Legon',
    quote:
      'AtadaBoard helped us scale from 200 to 800 students without adding administrative staff. Our team can focus on student care instead of routine tasks.',
  },
];

/*
  The three platform figures the previous site published. The old footer carried
  a second, conflicting set (500+ schools, 99.9% uptime, 98% satisfaction) that
  contradicted these; that set was dropped rather than reconciled by guesswork.
*/
export const PLATFORM_STATS = [
  { value: '20+', label: 'Schools running on AtadaBoard' },
  { value: '6,000+', label: 'Student records managed' },
  { value: '3,500+', label: 'Parents, teachers and staff' },
];
